import app from 'app';
import Analytics from 'c/WtAnalytics';

export class DuplicateController {
  /**
   * generic copy dialog
   * @param props {{story,source,message,storyStartPoint,...}}
   * @returns {Promise<unknown>}
   */
  static openDialog(props) {
    return new Promise((resolve) => {
      app.showModal({
        type: 'small',
        component: 'wt-make-copy',
        componentPropsData: {
          ...props,
          onCopySuccess(data) {
            resolve(data);
          },
        },
      });
    });
  }

  /**
   * generic copy function
   * @param payload {object} - context payload
   * @param options {object} - behaviour modifier
   * @returns {Promise<unknown>}
   */
  static duplicate(payload, options) {
    return new Promise((resolve, reject) => {
      if (
        app.storyStartPoint.DUPLICATE_IN_PROGRESS_STORY ===
        payload.storyStartPoint
      ) {
        DuplicateController.copyDraftToDraft(payload, options).then(
          resolve,
          reject
        );
      } else if (
        app.storyStartPoint.BACK_TO === payload.storyStartPoint ||
        app.storyStartPoint.INSTANT === payload.storyStartPoint
      ) {
        DuplicateController.copyProducedToDraft(payload, options).then(
          resolve,
          reject
        );
      } else {
        app.doctorController.report(
          `DuplicateController::duplicate -> '${payload.storyStartPoint}' storyStartPoint is not implemented`
        );
      }
    });
  }

  static copyProducedToDraft(payload, options) {
    return new Promise((resolve, reject) => {
      app.delayedSpinner.show({ delay: 0 });
      DuplicateController.validateProducedBeforeDuplicate(
        payload,
        options
      ).then(
        () => {
          const data = {
            storyId: payload.story.id,
            newTitle: payload.title,
            destProgrammerId: payload.programmerId,
            storyStartPoint: payload.storyStartPoint,
            brandId: payload.brandId,
            themeId: payload.themeId,
            resetCategories: payload.resetCategories,
          };
          let apiPromise = null;

          if (options.template) {
            if (options.keepAsStoryboard) {
              apiPromise =
                app.api.producedStoryTemplates.createDraftStoryTemplateFromProducedStoryTemplate(
                  data
                );
            } else {
              // "use this storyboard"
              apiPromise =
                app.api.producedStoryTemplates.createDraftStoryFromProducedStoryTemplate(
                  data
                );
            }
          } else {
            apiPromise =
              app.api.producedStories.createDraftStoryFromProducedStory(data);
          }

          apiPromise
            .then((res) => {
              resolve(res);
            })
            .catch(() => {
              app.delayedSpinner.hide();
              reject();
            });
        },
        () => {
          app.delayedSpinner.hide();
          reject();
        }
      );
    });
  }

  static copyDraftToDraft(payload, options) {
    return new Promise((resolve, reject) => {
      app.delayedSpinner.show({ delay: 0 });
      DuplicateController.validateDraftBeforeDuplicate(payload, options).then(
        () => {
          const data = {
            storyId: payload.story.id,
            newTitle: payload.title,
            destProgrammerId: payload.programmerId,
            storyStartPoint: payload.storyStartPoint,
            brandId: payload.brandId,
            themeId: payload.themeId,
          };

          const apiPromise = options.template
            ? app.api.draftStoryTemplates.createDraftStoryTemplateFromDraftStoryTemplate(
                data
              )
            : app.api.draftStories.createDraftStoryFromDraftStory(data);

          apiPromise
            .then((res) => {
              resolve(res);
              DuplicateController.copyDraftToDraft_Analytics(payload, options);
            })
            .catch(() => {
              app.delayedSpinner.hide();
              reject();
            });
        },
        () => {
          app.delayedSpinner.hide();
          reject();
        }
      );
    });
  }

  static validateDraftBeforeDuplicate(payload, options) {
    return new Promise((resolve, reject) => {
      app.api.draftStories
        .disabledCustomOverlays({
          storyId: payload.story.id,
          targetProgrammerId: payload.programmerId,
          themeId: payload.themeId,
        })
        .then((res) => {
          if (!_.isEmpty(res.disabledCustomOverlays)) {
            DuplicateController.validateFailed_ConfirmContinueAnyway(
              options
            ).then(resolve, reject);
          } else {
            resolve();
          }
        })
        .catch(reject);
    });
  }

  static validateProducedBeforeDuplicate(payload, options) {
    return new Promise((resolve, reject) => {
      app.api.producedStories
        .disabledCustomOverlays({
          storyId: payload.story.id,
          targetProgrammerId: payload.programmerId,
          themeId: payload.themeId,
        })
        .then((res) => {
          if (!_.isEmpty(res.disabledCustomOverlays)) {
            DuplicateController.validateFailed_ConfirmContinueAnyway(
              options
            ).then(resolve, reject);
          } else {
            resolve();
          }
        })
        .catch(reject);
    });
  }

  static validateFailed_ConfirmContinueAnyway({ modalCaption }) {
    return new Promise((resolve, reject) => {
      app.delayedSpinner.hide({ delay: 0 });
      app.dialog
        .error({
          type: app.dialog.types.ERROR_3,
          title: app.culture.confirm.coConflict.title,
          description:
            modalCaption || app.culture.confirm.coConflict.caption.default,
          showContactCsm: false,
          buttonYesText: 'Yes, continue',
          buttonNoText: 'Cancel',
        })
        .then((res) => {
          if (res.action) {
            resolve();
            app.delayedSpinner.show({ delay: 0 });
          } else {
            reject();
          }
        }, reject);
    });
  }

  static copyDraftToDraft_Analytics(payload, options) {
    const sameProgrammer = payload.programmerId === payload.story.programmerId;
    const analyticsParams = {
      Source:
        options.source === 'timeline' ? 'timeline menu' : 'dashboard thumbnail',
      ProductionLine: options.hasMoreThenOneProgrammer
        ? sameProgrammer
          ? 'same'
          : 'different'
        : 'no choice',
    };

    Analytics.trigger('analytics:makeCopy:storyDone', analyticsParams);
  }

  static rebrand(payload, options) {
    return new Promise((resolve, reject) => {
      app.delayedSpinner.show({ delay: 0 });

      DuplicateController.validateProducedBeforeDuplicate(
        payload,
        options
      ).then(
        () => {
          app.api.producedStories
            .createProducedStoryFromProducedStory({
              storyId: payload.story.id,
              newTitle: payload.story.title,
              destProgrammerId: payload.programmerId,
              storyStartPoint: payload.storyStartPoint,
              brandId: payload.brandId,
              themeId: payload.themeId,
            })
            .then(
              (res) => {
                console.log(
                  `Story Rebrand successfully , new id is '${res.storyId}'`
                );
                resolve(res);
                DuplicateController.rebrand_Analytics(payload, options);
              },
              () => {
                app.delayedSpinner.hide();
                reject();
              }
            );
        },
        () => {
          app.delayedSpinner.hide();
          reject();
        }
      );
    });
  }

  static rebrand_Analytics(payload, options) {
    Analytics.trigger('discover:rebrand', {
      videoID: payload.story.id,
      source: options.source,
    });
  }
}
